export type CalendarEvent = {
  title: string;
  description?: string;
  start: Date;
  end: Date;
  uid?: string;
  location?: string;
};

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function toGoogleDateParam(d: Date) {
  // Use UTC Zulu format: YYYYMMDDTHHmmssZ
  const iso = new Date(d).toISOString();
  return iso.replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
}

export function buildGoogleCalendarUrl(evt: CalendarEvent) {
  const start = toGoogleDateParam(evt.start);
  const end = toGoogleDateParam(evt.end);
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: evt.title,
    details: evt.description || '',
    dates: `${start}/${end}`,
  });
  if (evt.location) params.set('location', evt.location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function formatICSDate(d: Date) {
  const dt = new Date(d);
  return (
    dt.getUTCFullYear().toString() +
    pad2(dt.getUTCMonth() + 1) +
    pad2(dt.getUTCDate()) + 'T' +
    pad2(dt.getUTCHours()) +
    pad2(dt.getUTCMinutes()) +
    pad2(dt.getUTCSeconds()) + 'Z'
  );
}

export function buildICSContent(evt: CalendarEvent) {
  const uid = evt.uid || `${Date.now()}@hipo.coach`;
  const dtstamp = formatICSDate(new Date());
  const dtstart = formatICSDate(evt.start);
  const dtend = formatICSDate(evt.end);
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//HiPo AI Coach//MoM Action Ledger//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART:${dtstart}`,
    `DTEND:${dtend}`,
    `SUMMARY:${escapeICS(evt.title)}`,
    evt.description ? `DESCRIPTION:${escapeICS(evt.description)}` : '',
    evt.location ? `LOCATION:${escapeICS(evt.location)}` : '',
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean);
  return lines.join('\r\n');
}

function escapeICS(s: string) {
  return s
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
}

export function buildICSDataUrl(evt: CalendarEvent) {
  const content = buildICSContent(evt);
  return 'data:text/calendar;charset=utf-8,' + encodeURIComponent(content);
}
